import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, finalize, timer } from 'rxjs';
import { MyOskLectureAttendanceListService } from '../../../../api/api-services/my-osk-lecture-attendance-list.service';
import { MyOskLectureAttendanceListsService } from '../../../../api/api-services/my-osk-lecture-attendance-lists.service';
import { MyOskLectureAttendanceScreenService } from '../../../../api/api-services/my-osk-lecture-attendance-screen.service';
import {
  IMyOskLectureAttendanceListDetailsVm,
  IMyOskLectureAttendanceListVm,
  IMyOskLectureAttendanceMarkIm,
  IMyOskLectureAttendanceStudentVm,
  LectureAttendanceSource,
} from '../../../../api/my-osk-lecture-attendance-models.model';
import { canRunLectures } from '../../../shared/helpers/osk-staff-role';
import { parseUtc } from '../../../shared/helpers/utc-date';

/** What opens the dialog: the group, and optionally the list to show right away. */
export interface LectureAttendanceDialogData {
  lectureGroupGuid: string;
  lectureGroupName: string;
  /** Opens today's list at once - after the lectures were started on a station. */
  openToday?: boolean;
}

/** While the lectures run, the list follows the confirmations from the app at this pace. */
const LIVE_REFRESH_MS = 5_000;
const WEEKDAYS = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'];

/**
 * Attendance at the lectures of a group: the lecture days with the number of students present, and
 * the list of one day. Students confirm by scanning the QR code from the station in the app; the
 * lecturer marks here those who could not, and can bring the QR screen up on the station at any
 * moment of the lecture.
 */
@Component({
  selector: 'app-lecture-attendance-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CheckboxModule,
    ConfirmDialogModule,
    InputTextModule,
    TableModule,
    TagModule,
    TooltipModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './lecture-attendance-dialog.component.html',
  styleUrl: './lecture-attendance-dialog.component.scss',
})
export class LectureAttendanceDialogComponent implements OnInit, OnDestroy {
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly listsService = inject(MyOskLectureAttendanceListsService);
  private readonly listService = inject(MyOskLectureAttendanceListService);
  private readonly screenService = inject(MyOskLectureAttendanceScreenService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly data = this.dialogConfig.data as LectureAttendanceDialogData;
  readonly canRunLectures = canRunLectures();
  readonly Source = LectureAttendanceSource;
  readonly today = warsawToday();

  view: 'days' | 'list' = 'days';
  days: IMyOskLectureAttendanceListVm[] = [];
  daysLoading = false;
  addingDay = false;
  newDay = this.today;
  newTopic = '';

  list: IMyOskLectureAttendanceListDetailsVm | null = null;
  listLoading = false;
  saving = false;
  showingScreen = false;
  deleting = false;
  topic = '';
  /** Marks as the server last returned them, and what the user has on the screen. */
  private baseline = new Map<string, boolean>();
  marks = new Map<string, boolean>();
  private topicBaseline = '';
  private liveRefresh?: Subscription;
  private readonly requests = new Subscription();

  ngOnInit(): void {
    if (this.data.openToday) {
      this.openDay(this.today);
    } else {
      this.loadDays();
    }
  }

  ngOnDestroy(): void {
    this.stopLiveRefresh();
    this.requests.unsubscribe();
  }

  get anyRunning(): boolean {
    return this.days.some((day) => day.isRunning);
  }

  get presentCount(): number {
    return this.list ? this.list.students.filter((student) => this.isPresent(student)).length : 0;
  }

  get changedMarks(): IMyOskLectureAttendanceMarkIm[] {
    return [...this.marks.entries()]
      .filter(([studentGuid, isPresent]) => this.baseline.get(studentGuid) !== isPresent)
      .map(([studentGuid, isPresent]) => ({ studentGuid, isPresent }));
  }

  get dirty(): boolean {
    return this.changedMarks.length > 0 || this.topic.trim() !== this.topicBaseline;
  }

  loadDays(): void {
    this.daysLoading = true;
    this.requests.add(
      this.listsService
        .get(this.data.lectureGroupGuid)
        .pipe(finalize(() => (this.daysLoading = false)))
        .subscribe({
          next: (days) => (this.days = days),
          error: (error: HttpErrorResponse) => this.showError(error, 'Nie udało się pobrać wykładów grupy.'),
        })
    );
  }

  /** Opens (or creates) the list of the day - today by default. */
  openDay(lectureDay: string, topic?: string): void {
    this.listLoading = true;
    this.view = 'list';
    this.requests.add(
      this.listService
        .post({
          lectureGroupGuid: this.data.lectureGroupGuid,
          lectureDay,
          topic: topic?.trim() || null,
        })
        .pipe(finalize(() => (this.listLoading = false)))
        .subscribe({
          next: (list) => {
            this.addingDay = false;
            this.newTopic = '';
            this.applyList(list, true);
          },
          error: (error: HttpErrorResponse) => {
            this.showError(error, 'Nie udało się otworzyć listy obecności.');
            this.backToDays();
          },
        })
    );
  }

  openList(day: IMyOskLectureAttendanceListVm): void {
    this.listLoading = true;
    this.view = 'list';
    this.requests.add(
      this.listService
        .get(day.guid)
        .pipe(finalize(() => (this.listLoading = false)))
        .subscribe({
          next: (list) => this.applyList(list, true),
          error: (error: HttpErrorResponse) => {
            this.showError(error, 'Nie udało się pobrać listy obecności.');
            this.backToDays();
          },
        })
    );
  }

  addDay(): void {
    if (!this.newDay || this.newDay > this.today) {
      this.messageService.add({ severity: 'warn', detail: 'Wybierz dzisiejszy lub wcześniejszy dzień.' });
      return;
    }
    this.openDay(this.newDay, this.newTopic);
  }

  backToDays(): void {
    this.stopLiveRefresh();
    this.list = null;
    this.view = 'days';
    this.loadDays();
  }

  isPresent(student: IMyOskLectureAttendanceStudentVm): boolean {
    return this.marks.get(student.studentGuid) ?? false;
  }

  isChanged(student: IMyOskLectureAttendanceStudentVm): boolean {
    return this.baseline.get(student.studentGuid) !== this.marks.get(student.studentGuid);
  }

  setPresent(student: IMyOskLectureAttendanceStudentVm, isPresent: boolean): void {
    this.marks.set(student.studentGuid, isPresent);
  }

  markAll(isPresent: boolean): void {
    this.list?.students
      .filter((student) => student.isMember)
      .forEach((student) => this.marks.set(student.studentGuid, isPresent));
  }

  save(): void {
    if (!this.list || this.saving) {
      return;
    }
    this.saving = true;
    this.requests.add(
      this.listService
        .put({ guid: this.list.guid, topic: this.topic.trim() || null, marks: this.changedMarks })
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: (list) => {
            this.applyList(list, true);
            this.messageService.add({ severity: 'success', detail: 'Lista obecności została zapisana.' });
          },
          error: (error: HttpErrorResponse) => this.showError(error, 'Nie udało się zapisać listy obecności.'),
        })
    );
  }

  /** Brings the QR screen up on the station where the group's lectures run (or takes it down). */
  showOnStation(visible: boolean): void {
    if (this.showingScreen) {
      return;
    }
    this.showingScreen = true;
    this.requests.add(
      this.screenService
        .post({ lectureGroupGuid: this.data.lectureGroupGuid, visible })
        .pipe(finalize(() => (this.showingScreen = false)))
        .subscribe({
          next: () =>
            this.messageService.add({
              severity: 'success',
              detail: visible
                ? 'Lista obecności z kodem QR jest wyświetlana na stanowisku.'
                : 'Lista obecności została zamknięta na stanowisku.',
            }),
          error: (error: HttpErrorResponse) => this.showError(error, 'Nie udało się wyświetlić listy na stanowisku.'),
        })
    );
  }

  deleteList(): void {
    const list = this.list;
    if (!list) {
      return;
    }
    this.confirmationService.confirm({
      key: 'lectureAttendanceConfirm',
      header: 'Usuwanie listy obecności',
      message: `Usunąć listę obecności z dnia ${this.formatDay(list.lectureDay)}? Nikt nie jest na niej obecny.`,
      acceptLabel: 'Usuń',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () => {
        this.deleting = true;
        this.requests.add(
          this.listService
            .delete(list.guid)
            .pipe(finalize(() => (this.deleting = false)))
            .subscribe({
              next: () => {
                this.messageService.add({ severity: 'success', detail: 'Lista obecności została usunięta.' });
                this.backToDays();
              },
              error: (error: HttpErrorResponse) => this.showError(error, 'Nie udało się usunąć listy obecności.'),
            })
        );
      },
    });
  }

  close(): void {
    if (this.view !== 'list' || !this.dirty) {
      this.dialogRef.close();
      return;
    }
    this.confirmationService.confirm({
      key: 'lectureAttendanceConfirm',
      header: 'Niezapisane zmiany',
      message: 'Zaznaczenia na liście nie zostały zapisane. Zamknąć bez zapisywania?',
      acceptLabel: 'Zamknij bez zapisu',
      rejectLabel: 'Wróć',
      defaultFocus: 'reject',
      accept: () => this.dialogRef.close(),
    });
  }

  formatDay(lectureDay: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(lectureDay);
    if (!match) {
      return lectureDay;
    }
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return `${WEEKDAYS[date.getDay()]}, ${Number(match[3])}.${match[2]}.${match[1]}`;
  }

  markedAt(student: IMyOskLectureAttendanceStudentVm): Date | null {
    return parseUtc(student.markedUtc);
  }

  /**
   * Takes the list from the server. Marks the user changed and has not saved yet stay as they are,
   * so a confirmation arriving from the app while the lecturer ticks the list does not undo the ticks.
   */
  private applyList(list: IMyOskLectureAttendanceListDetailsVm, replaceEdits: boolean): void {
    const pending = replaceEdits ? new Map<string, boolean>() : new Map(this.changedMarks.map((mark) => [mark.studentGuid, mark.isPresent]));
    const topicEdited = !replaceEdits && this.topic.trim() !== this.topicBaseline;

    this.list = list;
    this.baseline = new Map(list.students.map((student) => [student.studentGuid, student.isPresent]));
    this.marks = new Map(this.baseline);
    pending.forEach((isPresent, studentGuid) => {
      if (this.baseline.has(studentGuid)) {
        this.marks.set(studentGuid, isPresent);
      }
    });
    this.topicBaseline = list.topic ?? '';
    if (!topicEdited) {
      this.topic = this.topicBaseline;
    }

    if (list.isRunning) {
      this.startLiveRefresh();
    } else {
      this.stopLiveRefresh();
    }
  }

  private startLiveRefresh(): void {
    if (this.liveRefresh) {
      return;
    }
    this.liveRefresh = timer(LIVE_REFRESH_MS, LIVE_REFRESH_MS).subscribe(() => {
      const list = this.list;
      if (!list || this.saving || this.view !== 'list') {
        return;
      }
      this.requests.add(
        this.listService.get(list.guid).subscribe({
          next: (fresh) => {
            if (this.list?.guid === fresh.guid && !this.saving) {
              this.applyList(fresh, false);
            }
          },
          // A missed refresh is caught up by the next one.
          error: () => undefined,
        })
      );
    });
  }

  private stopLiveRefresh(): void {
    this.liveRefresh?.unsubscribe();
    this.liveRefresh = undefined;
  }

  private showError(error: HttpErrorResponse, fallback: string): void {
    const detail = typeof error?.error?.message === 'string' && error.error.message ? error.error.message : fallback;
    this.messageService.add({ severity: 'error', summary: 'Błąd', detail });
  }
}

/** Today in Warsaw as yyyy-MM-dd (the lecture days are Warsaw days). */
export function warsawToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find((x) => x.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}
